import React, { useEffect, useRef, useState } from "react";
import type { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { DatePicker, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Pagination from "components/pagination";
import { apiCall } from "utils/apiClient";
import AssignPartnerModal from "./components/AssignPartnerModal";

type JobStatus =
  | "pending"
  | "assigned"
  | "in-progress"
  | "completed"
  | "cancelled"
  | string;

type PartnerStatus =
  | "not-assigned"
  | "pending-acceptance"
  | "accepted"
  | "rejected"
  | string;

interface JobRow {
  id: number | string;
  jobNumber?: string;
  jobTitle?: string;
  bookingId?: number | string | null;
  bookingCode?: string | null;
  status?: JobStatus;
  partnerStatus?: PartnerStatus;
  scheduledDate?: string | null;
  completedDate?: string | null;
  totalPartnerAmount?: number | string;
  servicesCount?: number;
}

const QUICK_FILTERS = [
  { key: "today", label: "Today" },
  { key: "this_week", label: "This Week" },
  { key: "this_month", label: "This Month" },
];

const ListJobs: React.FC = () => {
  const navigate = useNavigate();
  const antIcon = <LoadingOutlined style={{ fontSize: 48 }} spin />;
  const latestFetchRef = useRef(0);

  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dateRange, setDateRange] =
    useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobRow | null>(null);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  const toNumber = (value: unknown, fallback = 0): number => {
    if (value === null || value === undefined || value === "")
      return fallback;
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "-"
      : date.toLocaleDateString();
  };

  const formatStatus = (status?: JobStatus) => {
    if (!status) return "-";
    return status
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const fetchJobs = async () => {
    const fetchId = ++latestFetchRef.current;
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string> = {
        page: String(currentPage),
        limit: String(recordsPerPage),
      };

      if (selectedStatus) params.status = selectedStatus;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;

      if (dateRange?.[0]) {
        params.start_date = dateRange[0].format("YYYY-MM-DD");
      }
      if (dateRange?.[1]) {
        params.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      const result: any = await apiCall({
        endpoint: "partner-jobs",
        method: "GET",
        params,
      });

      if (fetchId !== latestFetchRef.current) return;

      if (result.status === 200) {
        const jobsData = Array.isArray(result.data)
          ? result.data
          : result.data?.data || [];

        const pagination =
          result.pagination || result.data?.pagination;

        const resolvedTotal =
          pagination?.total_records ?? jobsData.length;

        const normalizedJobs: JobRow[] = jobsData.map((item: any) => ({
          id: item.id,
          jobNumber: item.job_id ?? item.job_number ?? item.id ?? "-",
          jobTitle: item.title ?? item.job_title ?? item.jobTitle ?? "-",
          bookingId: item.booking_id ?? item.bookingId ?? null,
          bookingCode: item.booking_code ?? item.bookingCode ?? null,
          status: item.status ?? "",
          partnerStatus:
            item.partner_status ?? item.partnerStatus ?? "not-assigned",
          scheduledDate:
            item.estimated_start_date ?? item.scheduled_date ?? null,
          completedDate: item.completed_date ?? item.completedDate ?? null,
          totalPartnerAmount:
            item.total_partner_amount ?? item.totalPartnerAmount ?? 0,
          servicesCount:
            item.services_count ??
            item.items_count ??
            (Array.isArray(item.items)
              ? item.items.filter((entry: any) => !entry?.parent_item_id).length
              : 0),
        }));

        setJobs(normalizedJobs);
        setTotalRecords(resolvedTotal);
        setTotalPages(
          Math.max(
            1,
            pagination?.total_pages ??
              Math.ceil(resolvedTotal / recordsPerPage)
          )
        );
      } else {
        setJobs([]);
        setError(result.message || "Failed to fetch jobs");
      }
    } catch (err: any) {
      if (fetchId !== latestFetchRef.current) return;
      setJobs([]);
      setError(err?.message || "Failed to fetch jobs");
    } finally {
      if (fetchId !== latestFetchRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [
    currentPage,
    recordsPerPage,
    selectedStatus,
    debouncedSearchTerm,
    dateRange,
    quickFilter,
  ]);

  const handleOpenBooking = (job: JobRow) => {
    if (!job.bookingId) return;
    navigate("/booking-details", {
      state: { booking_id: job.bookingId },
    });
  };

  const handleOpenJobDetails = (job: JobRow) => {
    navigate("/job-details", { state: { job } });
  };

  const handleOpenAssignModal = (job: JobRow) => {
    setSelectedJob(job);
    setShowAssignModal(true);
  };

  const handleAssignPartner = async (
    partners: { id: number; name: string }[]
  ) => {
    if (!selectedJob) return;

    const partnerIds = partners.map((partner) => partner.id);
    const adminIdRaw = localStorage.getItem("id");
    const adminId = adminIdRaw ? Number(adminIdRaw) : null;

    try {
      const response = await apiCall({
        endpoint: "partner-jobs/assign",
        method: "POST",
        data: {
          job_id: selectedJob.id,
          partner_ids: partnerIds,
          assigned_by_admin_id: Number.isFinite(adminId)
            ? adminId
            : undefined,
        },
      });

      if (response.status === 200 || response.status === 201) {
        setShowAssignModal(false);
        setSelectedJob(null);
        fetchJobs();
      } else {
        alert(response.message || "Failed to assign partners");
      }
    } catch (err: any) {
      alert(err.message || "An error occurred while assigning partners");
      console.error("Assign partners error:", err);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading ? (
        <div className="flex justify-center items-center h-[70vh] w-full">
          <Spin indicator={antIcon} />
        </div>
      ) : (
        <div className="px-4 sm:px-6 py-6">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex justify-between">
              <h1 className="text-lg font-semibold">
                Job Management
              </h1>
              <div>
                Showing {jobs.length} of {totalRecords}
              </div>
            </div>

            <div className="overflow-x-auto mt-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-y border-gray-200">
                    <th className="px-5 py-3 text-left">Job No</th>
                    <th className="px-5 py-3 text-left">Title</th>
                    <th className="px-5 py-3 text-left">Booking</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-left">Scheduled</th>
                    <th className="px-5 py-3 text-right">Amount</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {jobs.length > 0 ? (
                    jobs.map((job) => (
                      <tr key={job.id}>
                        <td className="px-5 py-3">
                          {job.jobNumber}
                        </td>
                        <td className="px-5 py-3">
                          {job.jobTitle}
                        </td>
                        <td className="px-5 py-3">
                          {job.bookingId ? (
                            <button
                              onClick={() =>
                                handleOpenBooking(job)
                              }
                              className="text-blue-600 underline"
                            >
                              {job.bookingCode ||
                                `#${job.bookingId}`}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-5 py-3">
                          {formatStatus(job.status)}
                        </td>
                        <td className="px-5 py-3">
                          {formatDate(job.scheduledDate)}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {"\u20B9"}
                          {toNumber(
                            job.totalPartnerAmount
                          ).toLocaleString("en-IN")}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() =>
                                handleOpenJobDetails(job)
                              }
                              className="text-blue-600 hover:text-blue-700 p-1.5 hover:bg-blue-50 rounded"
                              title="View Details"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>

                            {job.partnerStatus === "not-assigned" && (
                              <button
                                onClick={() => handleOpenAssignModal(job)}
                                className="text-purple-600 hover:text-purple-700 p-1.5 hover:bg-purple-50 rounded"
                                title="Assign Partner"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                  />
                                </svg>
                              </button>
                            )}

                            <button className="text-gray-600 hover:text-gray-700 p-1.5 hover:bg-gray-50 rounded" title="Edit">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>

                            <button className="text-red-600 hover:text-red-700 p-1.5 hover:bg-red-50 rounded" title="Cancel Job">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-6"
                      >
                        No jobs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                recordsPerPage={recordsPerPage}
                onRecordsPerPageChange={(value) => {
                  setCurrentPage(1);
                  setRecordsPerPage(value);
                }}
                totalRecords={totalRecords}
                count={jobs.length}
              showFirstLastControls
              compact
              />
            </div>
          </div>
        </div>
      )}
      <AssignPartnerModal
        isOpen={showAssignModal && !!selectedJob}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedJob(null);
        }}
        jobTitle={selectedJob?.jobTitle || ""}
        jobNumber={selectedJob?.jobNumber || ""}
        servicesCount={selectedJob?.servicesCount || 0}
        onAssign={handleAssignPartner}
      />
    </DashboardLayout>
  );
};

export default ListJobs;
